import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authUtils';
import { resolveCoverUrl } from '../utils/coverUrl';
import useListeningTracker from '../hooks/useListeningTracker';

const STORAGE_KEY = 'sl_global_audio_state_v1';
const SLEEP_SNAPSHOT_KEY = 'sl_sleep_snapshot_v1';
const SLEEP_SNAPSHOT_TTL_MS = 24 * 60 * 60 * 1000; // 24h
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AudioPlayerContext = createContext(null);

const loadPersisted = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.track || !parsed.track.id) return null;
    return parsed;
  } catch {
    return null;
  }
};

const savePersisted = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* quota exceeded — ignore */
  }
};

const clearPersisted = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

const writeSleepSnapshot = (track, position, duration, reason) => {
  if (!track || !track.id) return;
  try {
    localStorage.setItem(
      SLEEP_SNAPSHOT_KEY,
      JSON.stringify({
        track,
        position: Math.max(0, position || 0),
        duration: duration || 0,
        reason, // 'timer' | 'end-of-track'
        savedAt: Date.now(),
      })
    );
  } catch {
    /* ignore */
  }
};

const readSleepSnapshot = () => {
  try {
    const raw = localStorage.getItem(SLEEP_SNAPSHOT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.track || !parsed.track.id) return null;
    if (Date.now() - (parsed.savedAt || 0) > SLEEP_SNAPSHOT_TTL_MS) {
      localStorage.removeItem(SLEEP_SNAPSHOT_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const clearSleepSnapshot = () => {
  try {
    localStorage.removeItem(SLEEP_SNAPSHOT_KEY);
  } catch {
    /* ignore */
  }
};

export const AudioPlayerProvider = ({ children }) => {
  const audioRef = useRef(null);
  const persisted = useMemo(loadPersisted, []);

  const [track, setTrack] = useState(persisted?.track || null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(persisted?.currentTime || 0);
  const [duration, setDuration] = useState(persisted?.duration || 0);
  const [volume, setVolumeState] = useState(persisted?.volume ?? 80);
  const [muted, setMutedState] = useState(false);
  const [playbackSpeed, setPlaybackSpeedState] = useState(
    persisted?.playbackSpeed || 1
  );

  // Listening metric — heartbeat tracker. Maps the player's track type
  // onto the listening_routes content type, and forwards the current
  // playback speed so the backend applies the speed-modified XP table.
  useListeningTracker({
    isPlaying,
    contentId: track?.id || null,
    contentType: track?.type === 'cinematic'
      ? 'cinematic'
      : track?.type === 'tts'
      ? 'tts'
      : 'audiobook',
    title: track?.title || '',
    speed: playbackSpeed,
  });
  // Sleep timer — `endAt` is an absolute ms timestamp, or null when off.
  // `endOfTrack` is true when the listener picked "End of current track" so we
  // pause once `onEnded` fires (and skip autoplay-next). Both are intentionally
  // NOT persisted to localStorage — the timer should reset every session so
  // listeners aren't surprised by audio cutting off after a refresh.
  const [sleepTimerEndAt, setSleepTimerEndAt] = useState(null);
  const [sleepTimerEndOfTrack, setSleepTimerEndOfTrack] = useState(false);
  const [sleepTimerNow, setSleepTimerNow] = useState(() => Date.now());
  // Bumped whenever a sleep snapshot is written so subscribed UIs (e.g. the
  // SleepResumeBanner) re-read localStorage and surface the resume offer
  // without polling.
  const [sleepResumeBumpAt, setSleepResumeBumpAt] = useState(0);
  // Tracks whether we still need to seek to a restored position once metadata
  // loads (avoids fighting with the user / next/prev navigation).
  const pendingSeekRef = useRef(persisted?.currentTime || 0);

  // Persist on relevant state changes.
  useEffect(() => {
    if (!track) {
      clearPersisted();
      return;
    }
    savePersisted({
      track,
      currentTime,
      duration,
      volume,
      playbackSpeed,
    });
  }, [track, currentTime, duration, volume, playbackSpeed]);

  // On first mount: if we restored a track from localStorage, attach its URL
  // to the global <audio> element so the mini-player's toggle actually works
  // after a hard refresh. We DON'T autoplay — browsers block that without a
  // user gesture — but pendingSeekRef carries the saved position so the user
  // resumes exactly where they left off.
  const didRestoreRef = useRef(false);
  useEffect(() => {
    if (didRestoreRef.current) return;
    didRestoreRef.current = true;
    const a = audioRef.current;
    if (!a || !persisted?.track?.url) return;
    pendingSeekRef.current = persisted.currentTime || 0;
    a.src = persisted.track.url;
    try {
      a.load();
    } catch {
      /* ignore */
    }
  }, [persisted]);

  // Keep the audio element's volume / rate / muted in sync with state.
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = Math.max(0, Math.min(1, volume / 100));
  }, [volume]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    // `muted` works on iOS Safari even when `volume` is read-only.
    a.muted = muted;
  }, [muted]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.playbackRate = playbackSpeed;
  }, [playbackSpeed]);

  // ───────────────────────────────────────────────── Media Session API
  // Populate OS-level lock-screen / Control Center / Bluetooth button
  // metadata so users can see the currently-playing episode + album art
  // and control playback without opening the app. Supported natively on
  // Android Chrome, iOS Safari 15+, most desktop browsers. On platforms
  // without support, the whole block silently no-ops.
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    if (!track) {
      try { navigator.mediaSession.metadata = null; } catch { /* ignore */ }
      return;
    }
    const backend = process.env.REACT_APP_BACKEND_URL || '';
    const resolveArt = (url) => {
      if (!url) return null;
      if (url.startsWith('http') || url.startsWith('data:')) return url;
      if (url.startsWith('/api/')) return `${backend}${url}`;
      if (url.startsWith('/uploads/')) return `${backend}/api${url}`;
      return url;
    };
    const art = resolveArt(track.cover_image || track.cover_image_url || track.series_cover_image);
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: track.title || 'Now Playing',
        artist: track.series_title || track.author || 'Smarter Learning Hub',
        album: track.series_title || 'Smarter Learning Hub',
        artwork: art
          ? [96, 192, 256, 384, 512].map((size) => ({
              src: art,
              sizes: `${size}x${size}`,
              type: 'image/png',
            }))
          : [],
      });
    } catch { /* MediaMetadata not supported */ }
  }, [track]);

  // Bind lock-screen transport buttons → our local play/pause/seek/skip.
  // Re-registered whenever those callbacks change (React recreates them
  // rarely, only on dependency changes; the handlers themselves are
  // cheap to install).
  useEffect(() => {
    if (!('mediaSession' in navigator)) return undefined;
    const ms = navigator.mediaSession;
    const tryBind = (action, handler) => {
      try { ms.setActionHandler(action, handler); } catch { /* not supported */ }
    };
    tryBind('play', () => {
      const a = audioRef.current;
      if (a) a.play().then(() => setIsPlaying(true)).catch(() => {});
    });
    tryBind('pause', () => {
      const a = audioRef.current;
      if (!a) return;
      a.pause();
      setIsPlaying(false);
    });
    tryBind('seekbackward', (details) => {
      const a = audioRef.current;
      if (!a) return;
      a.currentTime = Math.max(0, a.currentTime - (details?.seekOffset || 10));
    });
    tryBind('seekforward', (details) => {
      const a = audioRef.current;
      if (!a) return;
      a.currentTime = Math.min(a.duration || 0, a.currentTime + (details?.seekOffset || 10));
    });
    tryBind('seekto', (details) => {
      const a = audioRef.current;
      if (!a || details?.seekTime == null) return;
      if (details.fastSeek && a.fastSeek) a.fastSeek(details.seekTime);
      else a.currentTime = details.seekTime;
    });
    return () => {
      ['play', 'pause', 'seekbackward', 'seekforward', 'seekto'].forEach((a) => {
        try { ms.setActionHandler(a, null); } catch { /* ignore */ }
      });
    };
  }, []);

  // Keep the OS scrubber in sync with our audio position. Throttled to
  // ~2Hz so we don't burn cycles updating the OS on every timeupdate.
  useEffect(() => {
    if (!('mediaSession' in navigator) || !navigator.mediaSession.setPositionState) return undefined;
    const id = setInterval(() => {
      const a = audioRef.current;
      if (!a || !a.duration || !Number.isFinite(a.duration)) return;
      try {
        navigator.mediaSession.setPositionState({
          duration: a.duration,
          position: Math.min(a.currentTime, a.duration),
          playbackRate: a.playbackRate || 1,
        });
      } catch { /* ignore */ }
    }, 500);
    return () => clearInterval(id);
  }, [track?.id]);

  // Reflect playing/paused state back to the OS widget so the lock-screen
  // button shows the correct icon.
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    try {
      navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
    } catch { /* ignore */ }
  }, [isPlaying]);

  // Save backend progress every ~10s while playing + on unload.
  useEffect(() => {
    const save = (final = false) => {
      const a = audioRef.current;
      if (!a || !track?.id) return;
      const pos = a.currentTime;
      const dur = a.duration;
      if (!pos || !dur || dur < 1 || pos < 3) return;
      const progressPct = Math.min(100, Math.round((pos / dur) * 100));
      const payload = {
        book_id: track.id,
        progress: progressPct,
        last_position: pos,
      };
      try {
        if (final && navigator.sendBeacon) {
          navigator.sendBeacon(
            `${BACKEND_URL}/api/progress`,
            new Blob([JSON.stringify(payload)], { type: 'application/json' })
          );
        } else {
          authFetch(`${BACKEND_URL}/api/progress`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          }).catch(() => {});
        }
      } catch {
        /* best-effort */
      }
    };

    const interval = setInterval(() => {
      if (isPlaying) save(false);
    }, 10000);
    const onUnload = () => save(true);
    window.addEventListener('beforeunload', onUnload);
    window.addEventListener('pagehide', onUnload);
    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', onUnload);
      window.removeEventListener('pagehide', onUnload);
    };
  }, [isPlaying, track?.id]);

  // Audio element event handlers.
  const onTimeUpdate = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    setCurrentTime(a.currentTime);
  }, []);

  const onLoadedMetadata = useCallback(async () => {
    const a = audioRef.current;
    if (!a) return;
    setDuration(a.duration || 0);

    // Seek priority: pendingSeekRef (from track.last_position passed by load())
    // > server-saved progress > 0.
    if (pendingSeekRef.current && pendingSeekRef.current > 1) {
      try {
        a.currentTime = pendingSeekRef.current;
      } catch {
        /* ignore */
      }
      pendingSeekRef.current = 0;
      return;
    }

    if (!track?.id) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/api/progress`);
      if (r.ok) {
        const { progress } = await r.json();
        const row = (progress || []).find((p) => p.book_id === track.id);
        if (row && row.last_position && row.last_position > 5 && !row.completed) {
          a.currentTime = row.last_position;
          setCurrentTime(row.last_position);
        }
      }
    } catch {
      /* best-effort */
    }
  }, [track?.id]);

  const onPlay = useCallback(() => setIsPlaying(true), []);
  const onPause = useCallback(() => setIsPlaying(false), []);
  const onEndedRef = useRef(null);
  const onEnded = useCallback(() => {
    setIsPlaying(false);
    // If the listener has a "End of current track" sleep timer set, honour it
    // by skipping any registered onEnded handler (e.g. cinematic autoplay) and
    // clearing the timer so the next manual play starts fresh. Capture a
    // sleep snapshot so the resume banner can offer the next track later.
    if (sleepTimerEndOfTrack) {
      const cur = trackRef.current;
      const a = audioRef.current;
      if (cur && a) {
        writeSleepSnapshot(cur, a.duration || 0, a.duration || 0, 'end-of-track');
      }
      setSleepTimerEndOfTrack(false);
      setSleepTimerEndAt(null);
      setSleepResumeBumpAt(Date.now());
      return;
    }
    if (typeof onEndedRef.current === 'function') {
      try {
        onEndedRef.current();
      } catch {
        /* listener errors shouldn't break playback */
      }
    }
  }, [sleepTimerEndOfTrack]);

  // Public API ----------------------------------------------------
  // Load a track into the global audio element. If the same track is
  // already loaded, this is a no-op (preserves currentTime + isPlaying).
  // We read `track` via ref so this callback's identity stays stable —
  // otherwise Player's useEffect that calls load() would re-fire on every
  // track update and set up an infinite render loop.
  const trackRef = useRef(track);
  useEffect(() => {
    trackRef.current = track;
  }, [track]);

  const load = useCallback((nextTrack) => {
    if (!nextTrack || !nextTrack.url) return;
    const a = audioRef.current;
    if (!a) return;

    const cur = trackRef.current;
    if (cur && cur.id === nextTrack.id && a.src) {
      // Same track — keep playback going untouched.
      return;
    }

    // New track — set src and reset position.
    pendingSeekRef.current = nextTrack.last_position || 0;
    setTrack(nextTrack);
    setCurrentTime(nextTrack.last_position || 0);
    setDuration(0);
    a.src = nextTrack.url;
    try {
      a.load();
    } catch {
      /* ignore */
    }
  }, []);

  const play = useCallback(async () => {
    const a = audioRef.current;
    if (!a) return;
    try {
      await a.play();
      setIsPlaying(true);
    } catch (err) {
      console.warn('Play blocked:', err?.message || err);
      setIsPlaying(false);
    }
  }, []);

  const pause = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    a.pause();
    setIsPlaying(false);
  }, []);

  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      play();
    } else {
      pause();
    }
  }, [play, pause]);

  const seek = useCallback((seconds) => {
    const a = audioRef.current;
    if (!a) return;
    const safe = Math.max(0, Math.min(seconds, a.duration || seconds));
    try {
      a.currentTime = safe;
      setCurrentTime(safe);
    } catch {
      /* ignore */
    }
  }, []);

  const skip = useCallback((deltaSeconds) => {
    const a = audioRef.current;
    if (!a) return;
    seek((a.currentTime || 0) + deltaSeconds);
  }, [seek]);

  const setVolume = useCallback((v) => {
    const next = Math.max(0, Math.min(100, v));
    setVolumeState(next);
    // Auto-unmute when the user drags volume above 0; auto-mute when at 0.
    setMutedState(next === 0);
  }, []);

  const toggleMute = useCallback(() => {
    setMutedState((m) => !m);
  }, []);

  const setPlaybackSpeed = useCallback((s) => {
    setPlaybackSpeedState(s);
  }, []);

  const clear = useCallback(() => {
    const a = audioRef.current;
    if (a) {
      a.pause();
      a.removeAttribute('src');
      try {
        a.load();
      } catch {
        /* ignore */
      }
    }
    setTrack(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    pendingSeekRef.current = 0;
    clearPersisted();
  }, []);

  // Allow Player page to register an end-of-track handler (autoplay countdown).
  const setOnEnded = useCallback((fn) => {
    onEndedRef.current = fn;
  }, []);

  // ── MediaSession API ──────────────────────────────────────────────────────
  // Wires the global audio element to the OS-level controls (lock screen,
  // Bluetooth devices, AirPods, headset play/pause buttons, browser PiP, etc.)
  // so listeners get a real audiobook product feel on every device.
  const navigate = useNavigate();

  // 1. Metadata + action handlers — re-attach whenever the track changes.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) {
      return undefined;
    }
    if (!track) {
      try {
        navigator.mediaSession.metadata = null;
        navigator.mediaSession.playbackState = 'none';
      } catch {
        /* some browsers throw when clearing — safe to ignore */
      }
      return undefined;
    }

    const artworkUrl = resolveCoverUrl(track.cover);
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: track.title || 'Untitled',
        artist: track.author || track.series_title || 'Smarter Learning Hub',
        album: track.series_title || '',
        artwork: artworkUrl
          ? [
              { src: artworkUrl, sizes: '96x96', type: 'image/png' },
              { src: artworkUrl, sizes: '256x256', type: 'image/png' },
              { src: artworkUrl, sizes: '512x512', type: 'image/png' },
            ]
          : [],
      });
    } catch (err) {
      // MediaMetadata constructor missing on some older WebViews.
      console.warn('[mediaSession] metadata set failed:', err?.message || err);
    }

    const safeSet = (action, handler) => {
      try {
        navigator.mediaSession.setActionHandler(action, handler);
      } catch {
        /* unsupported action — silently skip */
      }
    };

    safeSet('play', () => play());
    safeSet('pause', () => pause());
    safeSet('stop', () => pause());
    safeSet('seekbackward', (details) => {
      const offset = (details && details.seekOffset) || 10;
      skip(-offset);
    });
    safeSet('seekforward', (details) => {
      const offset = (details && details.seekOffset) || 10;
      skip(offset);
    });
    safeSet('seekto', (details) => {
      if (details && typeof details.seekTime === 'number') {
        seek(details.seekTime);
      }
    });
    // Episode-skip — only register handlers when there's somewhere to go,
    // so the OS UI greys out the buttons accurately.
    safeSet(
      'previoustrack',
      track.prev_episode_id
        ? () => navigate(`/player/${track.prev_episode_id}?autoplay=1`)
        : null
    );
    safeSet(
      'nexttrack',
      track.next_episode_id
        ? () => navigate(`/player/${track.next_episode_id}?autoplay=1`)
        : null
    );

    return () => {
      // Don't aggressively clear — leaving handlers attached lets the OS
      // session survive a route swap without flickering. They get overwritten
      // on the next track change.
    };
  }, [track, play, pause, seek, skip, navigate]);

  // 2. playbackState — drives the OS play/pause icon state.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) {
      return;
    }
    try {
      navigator.mediaSession.playbackState = track
        ? isPlaying
          ? 'playing'
          : 'paused'
        : 'none';
    } catch {
      /* ignore */
    }
  }, [isPlaying, track]);

  // 3. positionState — keeps the OS scrubber in sync. Browsers throttle this
  // for us; updating on every state change is safe.
  useEffect(() => {
    if (
      typeof navigator === 'undefined' ||
      !('mediaSession' in navigator) ||
      typeof navigator.mediaSession.setPositionState !== 'function'
    ) {
      return;
    }
    if (!track || !duration || !Number.isFinite(duration) || duration <= 0) {
      return;
    }
    try {
      navigator.mediaSession.setPositionState({
        duration,
        position: Math.min(currentTime || 0, duration),
        playbackRate: playbackSpeed || 1,
      });
    } catch {
      /* some browsers throw on edge values — non-fatal */
    }
  }, [track, duration, currentTime, playbackSpeed]);
  // ──────────────────────────────────────────────────────────────────────────

  // ── Sleep Timer ──────────────────────────────────────────────────────────
  // Tick every second while a timed sleep is active so the UI countdown stays
  // accurate. When the deadline passes, pause playback and clear the timer.
  useEffect(() => {
    if (!sleepTimerEndAt) return undefined;
    const tick = () => {
      const now = Date.now();
      setSleepTimerNow(now);
      if (now >= sleepTimerEndAt) {
        // Fire pause without recreating the callback; reading the live audio
        // element directly avoids stale closure issues in the interval.
        const a = audioRef.current;
        if (a && !a.paused) {
          try { a.pause(); } catch { /* ignore */ }
        }
        // Snapshot WHERE the listener fell asleep so we can offer "Resume"
        // on next app open. Read currentTime straight off the element so we
        // capture the freshest position even if React state lagged behind.
        const cur = trackRef.current;
        if (cur && a) {
          writeSleepSnapshot(cur, a.currentTime || 0, a.duration || 0, 'timer');
        }
        setIsPlaying(false);
        setSleepTimerEndAt(null);
        setSleepResumeBumpAt(Date.now());
      }
    };
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [sleepTimerEndAt]);

  // Public API: setSleepTimer(null | { minutes } | { endOfTrack: true })
  const setSleepTimer = useCallback((option) => {
    if (!option) {
      setSleepTimerEndAt(null);
      setSleepTimerEndOfTrack(false);
      return;
    }
    if (option.endOfTrack) {
      setSleepTimerEndAt(null);
      setSleepTimerEndOfTrack(true);
      return;
    }
    if (typeof option.minutes === 'number' && option.minutes > 0) {
      setSleepTimerEndAt(Date.now() + option.minutes * 60 * 1000);
      setSleepTimerEndOfTrack(false);
    }
  }, []);

  const sleepTimerRemainingMs = sleepTimerEndAt
    ? Math.max(0, sleepTimerEndAt - sleepTimerNow)
    : 0;

  // ── Sleep Resume helpers ─────────────────────────────────────────────────
  // The banner reads the snapshot synchronously on mount; `sleepResumeBumpAt`
  // is just a render trigger so the banner re-checks localStorage when a new
  // snapshot is written mid-session.
  const getSleepSnapshot = useCallback(() => readSleepSnapshot(), []);
  const dismissSleepSnapshot = useCallback(() => {
    clearSleepSnapshot();
    setSleepResumeBumpAt(Date.now());
  }, []);
  // ────────────────────────────────────────────────────────────────────────

  const value = useMemo(
    () => ({
      track,
      isPlaying,
      currentTime,
      duration,
      volume,
      muted,
      playbackSpeed,
      load,
      play,
      pause,
      toggle,
      seek,
      skip,
      setVolume,
      toggleMute,
      setPlaybackSpeed,
      clear,
      setOnEnded,
      audioRef,
      // Sleep timer
      setSleepTimer,
      sleepTimerRemainingMs,
      sleepTimerEndOfTrack,
      sleepTimerActive: !!sleepTimerEndAt || sleepTimerEndOfTrack,
      // Sleep resume
      getSleepSnapshot,
      dismissSleepSnapshot,
      sleepResumeBumpAt,
    }),
    [
      track,
      isPlaying,
      currentTime,
      duration,
      volume,
      muted,
      playbackSpeed,
      load,
      play,
      pause,
      toggle,
      seek,
      skip,
      setVolume,
      toggleMute,
      setPlaybackSpeed,
      clear,
      setOnEnded,
      setSleepTimer,
      sleepTimerRemainingMs,
      sleepTimerEndOfTrack,
      sleepTimerEndAt,
      getSleepSnapshot,
      dismissSleepSnapshot,
      sleepResumeBumpAt,
    ]
  );

  return (
    <AudioPlayerContext.Provider value={value}>
      {/* Single global audio element — survives route changes, so audio keeps
          playing while the user browses the rest of the app. */}
      <audio
        ref={audioRef}
        onTimeUpdate={onTimeUpdate}
        onLoadedMetadata={onLoadedMetadata}
        onPlay={onPlay}
        onPause={onPause}
        onEnded={onEnded}
        preload="metadata"
        data-testid="global-audio-element"
      />
      {children}
    </AudioPlayerContext.Provider>
  );
};

export const useAudioPlayer = () => {
  const ctx = useContext(AudioPlayerContext);
  if (!ctx) {
    throw new Error('useAudioPlayer must be used inside <AudioPlayerProvider>');
  }
  return ctx;
};

export default AudioPlayerContext;
